<template>
  <div ref="domRoot">
    <div class="scrollbar-hide max-w-[800px] min-w-[600px] overflow-y-scroll px-6 pb-11">
      <AlertBox v-if="props.errorMessage" type="danger" class="error-box mt-5" :icon-path="mdiAlertOctagon">
        {{ props.errorMessage }}
      </AlertBox>
      <AlertBox v-if="props.paymentInfoError" type="warning" class="error-box mt-5" :icon-path="mdiAlertOctagon">
        {{ props.paymentInfoError }}
      </AlertBox>
      <p class="mt-4 mb-5 txt-body-m-medium">{{ typed_t('ticketForm.ticketData') }}</p>

      <TicketDetailsFieldset
        v-if="userProperties"
        :user-properties="userProperties"
        :hidden-fields="hiddenFields"
        :optional-fields="optionalFields"
      />

      <!-- Extra fields NOT covered by TicketDetailsFieldset -->
      <div v-if="showBirthdate || hasPartnerAttributes" class="mt-2 grid grid-cols-2 gap-2">
        <DateInput
          v-if="showBirthdate"
          class="col-span-2"
          name="birthdate"
          :label="typed_t('ticketForm.birthdate') + (isBirthdateRequired ? ' *' : '')"
        />
        <TextInput v-if="hasPartnerAttributes" name="partnerName" :dense="true" :label="typed_t('ticketForm.partnerName') + ' *'" />
        <TextInput v-if="hasPartnerAttributes" name="partnerRole" :dense="true" :label="typed_t('ticketForm.partnerRole') + ' *'" />
      </div>

      <PaymentInfoSection
        v-if="needsPaymentInfo && paymentInfo && !props.paymentInfoError"
        ref="paymentInfoRef"
        v-model="paymentInfo"
        :quota-kind="props.invitationQuota.kind"
      />
    </div>
    <div class="mt-6 flex justify-end px-6 pb-6">
      <button class="btn-secondary-color-s" data-testid="iq-ticket-cancel" @click="emit('closeDialog')">
        {{ typed_t('ticketForm.cancel') }}
      </button>
      <button :disabled="props.ticket?.swapped" class="btn-primary-color-s ml-4" data-testid="iq-ticket-save" @click="submitForm">
        {{ typed_t('ticketForm.save') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiAlertOctagon } from '@mdi/js'
import { TicketDetailsFieldset, useTicketDetailsFieldsetSchema } from '@ramp106/omrjs-events-ui'
import { TextInput, DateInput } from '@ramp106/omrjs-vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { type AnySchema, object, string } from 'yup'

import { useUserPropertiesPreferencesQuery, type InvitationQuotaQuery, type TicketQuery } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import typed_translation from '@/helpers/typed_translation'

import PaymentInfoSection, { type PaymentInfoData } from './PaymentInfoSection.vue'
import useQuotaConfig, { type InternalQuotaTypeEnum } from './quotaConfig'

type Ticket = NonNullable<TicketQuery['ticket']>

const props = defineProps<{
  errorMessage: string
  invitationQuota: InvitationQuotaQuery['invitationQuota']
  paymentInfoError?: string
  ticket?: Ticket
}>()

const emit = defineEmits<{
  closeDialog: [value: void]
  submitForm: [value: Record<string, unknown>]
}>()

const { t } = useI18n()
const typed_t = typed_translation('InvitationQuota', props.invitationQuota.kind)

const domRoot = ref<HTMLDivElement | null>(null)
const paymentInfoRef = ref<InstanceType<typeof PaymentInfoSection> | null>(null)

const quotaConfig = useQuotaConfig(props.invitationQuota.kind as InternalQuotaTypeEnum)
const hasPartnerAttributes = computed(() => quotaConfig.value.hasPartnerAttributes)
const needsPaymentInfo = computed(() => quotaConfig.value.needsPaymentInfo)
const isBirthdateRequired = computed(() => quotaConfig.value.requiredTicketAttributes.includes('birthdate'))
const showBirthdate = computed(() => quotaConfig.value.additonalTicketAttributes.includes('birthdate'))

const hiddenFields = computed(() => {
  const { hasJobRelatedAttributes, needsCountry, additonalTicketAttributes } = quotaConfig.value
  return [
    !additonalTicketAttributes.includes('title') && 'title',
    !hasJobRelatedAttributes && 'jobLevel',
    !needsCountry && !hasJobRelatedAttributes && 'country',
    !additonalTicketAttributes.includes('phone') && 'phone',
  ].filter(Boolean) as string[]
})

const optionalFields = computed(() => {
  const { requiredTicketAttributes, additonalTicketAttributes } = quotaConfig.value
  const isPhoneOptional = additonalTicketAttributes.includes('phone') && !requiredTicketAttributes.includes('phone')
  return isPhoneOptional ? ['phone'] : []
})

const { schema: baseSchema, requiredMessage } = useTicketDetailsFieldsetSchema(
  computed(() => ({ hiddenFields: hiddenFields.value, optionalFields: optionalFields.value })),
)

const validationSchema = computed(() => {
  const base = baseSchema.value

  if (!showBirthdate.value && !hasPartnerAttributes.value) {
    return toTypedSchema(base)
  }

  const req = requiredMessage.value
  const extraShape: Record<string, AnySchema> = {}

  if (showBirthdate.value) {
    extraShape.birthdate = isBirthdateRequired.value ? string().required(req) : string()
  }

  if (hasPartnerAttributes.value) {
    extraShape.partnerName = string().required(req)
    extraShape.partnerRole = string().required(req)
  }

  return toTypedSchema(base.concat(object().shape(extraShape)))
})

const { handleSubmit, setFieldError, resetForm, validate } = useForm<Record<string, unknown>>({
  validationSchema,
})

function getInitialPaymentInfo(): PaymentInfoData | undefined {
  if (!needsPaymentInfo.value) return undefined

  const info = props.ticket?.paymentInfo
  return {
    business: info?.business ?? false,
    company: info?.company ?? '',
    street: info?.street ?? '',
    number: info?.number ?? '',
    additionalAddress: info?.additionalAddress ?? '',
    zip: info?.zip ?? '',
    city: info?.city ?? '',
    country: info?.country ?? props.ticket?.country ?? '',
    vatNumber: info?.vatNumber ?? '',
    customerReference: info?.customerReference ?? '',
  }
}

const paymentInfo = ref<PaymentInfoData | undefined>(getInitialPaymentInfo())

const { result } = useUserPropertiesPreferencesQuery()
const userProperties = computed(() => result.value?.userPropertiesPreferences)

watch(
  () => props.errorMessage,
  async (msg) => {
    if (msg) {
      setFieldError('email', msg)
      await nextTick()
      scrollToErrors()
    }
  },
)

onMounted(() => {
  if (!props.ticket) return

  const ticket = props.ticket
  resetForm({
    values: {
      locale: ticket.locale ?? undefined,
      salutation: ticket.salutation ?? undefined,
      firstName: ticket.firstName ?? '',
      lastName: ticket.lastName ?? '',
      email: ticket.email ?? '',
      phone: ticket.phone ?? '',
      country: ticket.country ?? (!quotaConfig.value.needsCountry ? 'NOT_PROVIDED' : ''),
      jobLevel: ticket.jobLevel ?? '',
      companyName: ticket.companyName ?? '',
      companyType: ticket.companyType ?? undefined,
      companyIndustryId: ticket.companyIndustryId ?? '',
      companyFieldId: ticket.companyFieldId ?? '',
      companyDepartment: ticket.companyDepartment ?? undefined,
      companySize: ticket.companySize ?? undefined,
      position: ticket.position ?? '',
      title: ticket.title ?? '',
      birthdate: ticket.birthdate ?? undefined,
      partnerName: ticket.partnerName ?? '',
      partnerRole: ticket.partnerRole ?? '',
    },
  })

  if (ticket.aasmState === 'unlocked') {
    validate()
  }
})

function scrollToErrors() {
  const root = domRoot.value
  if (!root) return

  const target = root.querySelector('.error-box') ?? root.querySelector('.text-error')
  target?.scrollIntoView({ behavior: 'smooth' })
}

const submitForm = handleSubmit(
  async (formValues) => {
    if (paymentInfoRef.value) {
      const paymentValid = await paymentInfoRef.value.validate()
      if (!paymentValid) {
        scrollToErrors()
        return
      }
    }

    const values = formValues as Record<string, unknown>
    emit('submitForm', {
      ...values,
      companyFieldId: values.companyFieldId || undefined,
      companyIndustryId: values.companyIndustryId || undefined,
      paymentInfo: paymentInfo.value,
    })
  },
  () => {
    scrollToErrors()
    showNotification(t('InvitationQuota.ticketForm.badFormNotification'), 'error', 3000)
  },
)
</script>
